import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/loanService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CATEGORIES = [
  ['general', 'General Enquiry'], ['loan_query', 'Loan Query'],
  ['repayment_issue', 'Repayment Issue'], ['kyc_issue', 'KYC Issue'],
  ['disbursement', 'Disbursement'], ['fraud_report', 'Fraud Report'],
  ['account', 'Account Issue'],
];

const STATUS_COLORS = {
  open:               { bg: 'rgba(91,142,255,0.12)', color: '#5B8EFF' },
  in_progress:        { bg: 'rgba(245,166,35,0.12)', color: '#F5A623' },
  awaiting_customer:  { bg: 'rgba(129,140,248,0.12)', color: '#818CF8' },
  resolved:           { bg: 'rgba(0,200,150,0.12)', color: '#00C896' },
  closed:             { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.open;
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 99, textTransform: 'uppercase', background: s.bg, color: s.color }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function NewTicketForm({ onSuccess }) {
  const [form, setForm] = useState({ category: 'general', subject: '', description: '' });
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (d) => supportService.createTicket(d),
    onSuccess: () => { onSuccess(); },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to create ticket.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) { setErr('Subject and description are required.'); return; }
    mutation.mutate(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Category</label>
        <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
          {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Subject</label>
        <input className="form-input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description of your issue" />
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={4} value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="Provide as much detail as possible — loan reference, dates, amounts…" />
      </div>
      {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending}>
          {mutation.isPending ? 'Submitting…' : 'Submit Ticket'}
        </button>
      </div>
    </form>
  );
}

function TicketThread({ ticket }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [err, setErr] = useState('');

  const replyMutation = useMutation({
    mutationFn: (msg) => supportService.addMessage(ticket.id, { message: msg }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ticket', ticket.id] }); setReply(''); setErr(''); },
    onError: (e) => setErr(e.response?.data?.message || 'Reply failed.'),
  });

  const { data } = useQuery({
    queryKey: ['ticket', ticket.id],
    queryFn: () => supportService.getTicket(ticket.id).then(r => r.data.data),
  });

  const full = data || ticket;
  const messages = full.messages || [];
  const canReply = !['closed'].includes(full.status);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-3)', marginBottom: '0.25rem' }}>{full.reference}</div>
          <h3 style={{ fontWeight: 700, fontSize: '0.975rem', marginBottom: '0.25rem' }}>{full.subject}</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <StatusBadge status={full.status} />
            <span style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{full.category?.replace(/_/g, ' ')}</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', maxHeight: 360, overflowY: 'auto' }}>
        {messages.map(m => (
          <div key={m.id} style={{
            padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
            background: m.is_staff ? 'rgba(91,142,255,0.08)' : 'rgba(0,200,150,0.06)',
            border: `1px solid ${m.is_staff ? 'rgba(91,142,255,0.2)' : 'rgba(0,200,150,0.15)'}`,
            alignSelf: m.is_staff ? 'flex-start' : 'flex-end',
            maxWidth: '85%',
          }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>
              <strong style={{ color: m.is_staff ? '#5B8EFF' : '#00C896' }}>{m.is_staff ? `Support — ${m.sender_name}` : 'You'}</strong>
              {' · '}{formatDate(m.created_at)}
            </div>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--text-1)', margin: 0 }}>{m.message}</p>
          </div>
        ))}
        {messages.length === 0 && (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', textAlign: 'center', padding: '1rem' }}>No messages yet.</p>
        )}
      </div>

      {canReply && (
        <>
          {err && <div className="alert alert-error" style={{ marginBottom: '0.5rem' }}><span>⚠</span><span>{err}</span></div>}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <textarea className="form-input" rows={2} value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Add a reply…"
              style={{ flex: 1, resize: 'none' }} />
            <button className="btn btn-primary" style={{ alignSelf: 'flex-end' }}
              disabled={!reply.trim() || replyMutation.isPending}
              onClick={() => replyMutation.mutate(reply)}>
              {replyMutation.isPending ? '…' : 'Send'}
            </button>
          </div>
        </>
      )}
      {!canReply && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', padding: '0.5rem 0', textAlign: 'center' }}>
          This ticket is closed.
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const qc = useQueryClient();
  const [view, setView] = useState('list'); // 'list' | 'new' | ticket-id
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => supportService.getTickets().then(r => r.data.data),
  });

  const tickets = data || [];

  const handleNewSuccess = () => {
    qc.invalidateQueries({ queryKey: ['my-tickets'] });
    setView('list');
  };

  if (view === 'new') {
    return (
      <div>
        <div className="page-header" style={{ marginBottom: '1.25rem' }}>
          <div>
            <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem', marginBottom: '0.5rem' }} onClick={() => setView('list')}>← Back</button>
            <h1>New Support Ticket</h1>
            <p>Describe your issue and our team will respond within 24 hours</p>
          </div>
        </div>
        <div className="card" style={{ maxWidth: 560 }}>
          <NewTicketForm onSuccess={handleNewSuccess} />
        </div>
      </div>
    );
  }

  if (view === 'ticket' && selected) {
    return (
      <div>
        <div className="page-header" style={{ marginBottom: '1.25rem' }}>
          <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem' }} onClick={() => setView('list')}>← Back to Tickets</button>
        </div>
        <div className="card" style={{ maxWidth: 640 }}>
          <TicketThread ticket={selected} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Support</h1>
          <p>Raise issues, track tickets, and chat with our support team</p>
        </div>
        <button className="btn btn-primary" onClick={() => setView('new')}>New Ticket</button>
      </div>

      {isLoading ? <LoadingSpinner /> : (
        tickets.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>◎</div>
            <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No support tickets yet</h3>
            <p style={{ color: 'var(--text-3)', marginBottom: '1.25rem', fontSize: '0.875rem' }}>Have a question or issue? We're here to help.</p>
            <button className="btn btn-primary" onClick={() => setView('new')}>Open a Ticket</button>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Reference</th><th>Subject</th><th>Category</th><th>Status</th><th>Messages</th><th>Updated</th><th></th></tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => { setSelected(t); setView('ticket'); }}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>{t.reference}</td>
                      <td style={{ fontWeight: 500, fontSize: '0.875rem', maxWidth: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)', textTransform: 'capitalize' }}>{t.category?.replace(/_/g, ' ')}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td style={{ fontSize: '0.875rem', textAlign: 'center' }}>{t.message_count}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(t.updated_at)}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 600 }}>→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
}
